// A small History API router. No dependency: pushState plus popstate, one
// subscription store read through useSyncExternalStore, and pattern matching
// for ":param" segments. The web server falls back to index.html for every
// unknown path, so deep links such as /dokumen/<id> load straight in.
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== "undefined") window.addEventListener("popstate", notify);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") window.removeEventListener("popstate", notify);
  };
}

function snapshot(): string {
  if (typeof location === "undefined") return "/";
  return location.pathname + location.search;
}

export interface Location {
  path: string;
  query: URLSearchParams;
  href: string;
}

function toLocation(href: string): Location {
  const q = href.indexOf("?");
  const path = normalize(q === -1 ? href : href.slice(0, q));
  return { path, query: new URLSearchParams(q === -1 ? "" : href.slice(q + 1)), href };
}

function normalize(path: string): string {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
}

export function useLocation(): Location {
  const href = useSyncExternalStore(subscribe, snapshot, () => "/");
  return toLocation(href);
}

export interface NavigateOptions {
  replace?: boolean;
}

export function navigate(to: string, opts: NavigateOptions = {}): void {
  if (typeof history === "undefined") return;
  if (to === snapshot()) return;
  if (opts.replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
    try {
      window.scrollTo(0, 0);
    } catch {
      // happy-dom and some embedded views do not implement scrollTo.
    }
  }
  notify();
}

/** Replaces only the query string of the current path (filters, search). */
export function setQuery(params: Record<string, string | undefined>): void {
  const current = toLocation(snapshot());
  const next = new URLSearchParams(current.query);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") next.delete(k);
    else next.set(k, v);
  }
  const qs = next.toString();
  const to = qs ? `${current.path}?${qs}` : current.path;
  if (to === snapshot()) return;
  history.replaceState(null, "", to);
  notify();
}

/** Params of `pattern` ("/dokumen/:id") against `path`, or null. */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const a = normalize(pattern).split("/");
  const b = normalize(path).split("/");
  if (a.length !== b.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < a.length; i++) {
    const seg = a[i]!;
    const val = b[i]!;
    if (seg.startsWith(":")) {
      if (!val) return null;
      try {
        params[seg.slice(1)] = decodeURIComponent(val);
      } catch {
        return null;
      }
    } else if (seg !== val) return null;
  }
  return params;
}

/** True when a click on this anchor should be handled by the router. */
export function isRouterClick(e: { defaultPrevented: boolean; button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }, a: HTMLAnchorElement): boolean {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  const raw = a.getAttribute("href");
  if (!raw || raw.startsWith("#") || a.hasAttribute("download")) return false;
  const target = a.getAttribute("target");
  if (target && target !== "_self") return false;
  if (a.origin !== location.origin) return false;
  return true;
}

/**
 * Routes every same-origin <a href> click inside the document, including the
 * AppShell destinations (plain anchors rendered by packages/ui). Returns the
 * cleanup.
 */
export function interceptLinks(root: Document | HTMLElement = document): () => void {
  const onClick = (e: Event) => {
    const me = e as unknown as globalThis.MouseEvent;
    const el = (me.target as Element | null)?.closest?.("a");
    if (!el || !(el instanceof HTMLAnchorElement)) return;
    if (!isRouterClick(me, el)) return;
    me.preventDefault();
    navigate(el.pathname + el.search);
  };
  root.addEventListener("click", onClick);
  return () => root.removeEventListener("click", onClick);
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: ReactNode;
}

export function Link({ href, onClick, children, ...rest }: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (isRouterClick(e, e.currentTarget)) {
      e.preventDefault();
      navigate(href);
    }
  };
  return (
    <a href={href} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
